import { useDraggable } from "@dnd-kit/core";
import { CSS } from "@dnd-kit/utilities";
import { CodeBlock, BLOCK_CONFIGS } from "./CodeBlock";
import type { CodePaletteProps, BlockType, CodeBlockData } from "./types";

/**
 * Default blocks available in the palette
 */
const DEFAULT_BLOCKS: BlockType[] = [
  "move_up",
  "move_down",
  "move_left",
  "move_right",
];

/**
 * Generate a unique ID for new blocks
 */
export function generateBlockId(): string {
  return `block-${Date.now()}-${Math.random().toString(36).substring(2, 9)}`;
}

/**
 * Create a new block instance from a type
 */
export function createBlock(type: BlockType): CodeBlockData {
  return {
    id: generateBlockId(),
    type,
    ...(type === "repeat" ? { repeatCount: 2, children: [] } : {}),
  };
}

interface DraggablePaletteBlockProps {
  type: BlockType;
  disabled?: boolean;
}

/**
 * A draggable block in the palette
 */
function DraggablePaletteBlock({ type, disabled }: DraggablePaletteBlockProps) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    isDragging,
  } = useDraggable({
    id: `palette-${type}`,
    data: {
      type: "palette",
      blockType: type,
    },
    disabled,
  });

  const style = transform
    ? {
        transform: CSS.Translate.toString(transform),
        zIndex: isDragging ? 100 : undefined,
      }
    : undefined;

  // Create a display block for the palette
  const paletteBlock: CodeBlockData = {
    id: `palette-${type}`,
    type,
    ...(type === "repeat" ? { repeatCount: 2 } : {}),
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      {...listeners}
      {...attributes}
      className="touch-manipulation"
    >
      <CodeBlock
        block={paletteBlock}
        isDragging={isDragging}
        isPalette
        disabled={disabled}
      />
    </div>
  );
}

/**
 * CodePalette Component
 *
 * A container of available code blocks that can be dragged into the program.
 * Styled as a friendly "toolbox" with copper/orange tech aesthetic.
 */
export function CodePalette({
  availableBlocks = DEFAULT_BLOCKS,
  disabled = false,
}: CodePaletteProps) {
  // Separate direction blocks from special blocks (like repeat)
  const directionBlocks = availableBlocks.filter(
    (type) => BLOCK_CONFIGS[type].color === "direction"
  );
  const specialBlocks = availableBlocks.filter(
    (type) => BLOCK_CONFIGS[type].color === "loop"
  );

  return (
    <div
      className="
        relative
        bg-gradient-to-br from-pixel-light/30 via-white to-pixel-light/20
        rounded-3xl
        p-5
        border-4 border-pixel/40
        shadow-[0_6px_24px_rgba(255,140,66,0.15),inset_0_2px_0_rgba(255,255,255,0.9)]
      "
      role="region"
      aria-label="Available code blocks"
    >
      {/* Header with gear decoration */}
      <div className="flex items-center gap-3 mb-4">
        <div
          className="
            flex items-center justify-center
            w-10 h-10
            bg-gradient-to-br from-pixel to-pixel-dark
            rounded-xl
            border-2 border-pixel-dark
            shadow-[0_2px_0_0_theme(colors.pixel.dark)]
          "
        >
          <span className="text-xl">🧰</span>
        </div>
        <h3
          className="
            font-display font-bold text-xl
            text-pixel-dark
            tracking-wide
          "
        >
          Toolbox
        </h3>

        {/* Decorative gears */}
        <div className="flex-1" />
        <div className="text-lg opacity-40 animate-spin-slow">⚙️</div>
        <div
          className="text-sm opacity-30 animate-spin-slow-reverse"
          style={{ animationDelay: "0.5s" }}
        >
          ⚙️
        </div>
      </div>

      {/* Instruction text */}
      <p className="font-body text-sm text-neutral-500 mb-4 ml-1">
        Drag blocks to build your program!
      </p>

      {/* Direction blocks section */}
      {directionBlocks.length > 0 && (
        <div className="mb-4">
          <div className="flex items-center gap-2 mb-3 ml-1">
            <div className="w-3 h-3 rounded-full bg-sky-400 border border-sky-500" />
            <span className="font-body font-semibold text-xs text-neutral-500 uppercase tracking-wider">
              Movement
            </span>
          </div>
          <div className="grid grid-cols-2 gap-3">
            {directionBlocks.map((type) => (
              <DraggablePaletteBlock
                key={type}
                type={type}
                disabled={disabled}
              />
            ))}
          </div>
        </div>
      )}

      {/* Special blocks section (loops) */}
      {specialBlocks.length > 0 && (
        <div>
          <div className="flex items-center gap-2 mb-3 ml-1">
            <div className="w-3 h-3 rounded-full bg-emerald-400 border border-emerald-500" />
            <span className="font-body font-semibold text-xs text-neutral-500 uppercase tracking-wider">
              Loops
            </span>
          </div>
          <div className="flex flex-col gap-3">
            {specialBlocks.map((type) => (
              <DraggablePaletteBlock
                key={type}
                type={type}
                disabled={disabled}
              />
            ))}
          </div>
        </div>
      )}

      {/* Circuit pattern decoration */}
      <div className="absolute bottom-3 right-3 opacity-20 pointer-events-none">
        <svg width="48" height="48" viewBox="0 0 48 48" fill="none">
          <path
            d="M8 8 L40 8 M40 8 L40 24 M40 24 L24 24 M24 24 L24 40 M24 40 L8 40"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            className="text-pixel-dark"
          />
          <circle cx="8" cy="8" r="3" fill="currentColor" className="text-pixel" />
          <circle cx="40" cy="8" r="3" fill="currentColor" className="text-pixel" />
          <circle cx="40" cy="24" r="3" fill="currentColor" className="text-pixel" />
          <circle cx="24" cy="24" r="3" fill="currentColor" className="text-pixel" />
          <circle cx="24" cy="40" r="3" fill="currentColor" className="text-pixel" />
          <circle cx="8" cy="40" r="3" fill="currentColor" className="text-pixel" />
        </svg>
      </div>
    </div>
  );
}

export default CodePalette;
